/**
 * High-confidence SQL keyword / operator typos.
 * Oracle often misreports these as ORA-00907, ORA-00923, ORA-00933, etc.
 */

function offsetToLineCol(str, offset) {
  const before = str.substring(0, Math.max(0, offset));
  const lines = before.split(/\r?\n/);
  return { line: lines.length, column: (lines[lines.length - 1] || '').length + 1 };
}

/** Strip line and block comments for scanning (keep length via spaces). */
function stripCommentsKeepLen(sql) {
  let out = '';
  let i = 0;
  const s = sql;
  while (i < s.length) {
    if (s[i] === '-' && s[i + 1] === '-') {
      out += '  ';
      i += 2;
      while (i < s.length && s[i] !== '\n') {
        out += ' ';
        i++;
      }
      continue;
    }
    if (s[i] === '/' && s[i + 1] === '*') {
      out += '  ';
      i += 2;
      while (i < s.length && !(s[i] === '*' && s[i + 1] === '/')) {
        out += s[i] === '\n' ? '\n' : ' ';
        i++;
      }
      if (i < s.length) {
        out += '  ';
        i += 2;
      }
      continue;
    }
    if (s[i] === "'") {
      out += "'";
      i++;
      while (i < s.length) {
        if (s[i] === "'" && s[i + 1] === "'") {
          out += '  ';
          i += 2;
          continue;
        }
        if (s[i] === "'") {
          out += "'";
          i++;
          break;
        }
        out += s[i] === '\n' ? '\n' : ' ';
        i++;
      }
      continue;
    }
    if (s[i] === '"') {
      out += '"';
      i++;
      while (i < s.length) {
        if (s[i] === '"') {
          out += '"';
          i++;
          break;
        }
        out += s[i] === '\n' ? '\n' : ' ';
        i++;
      }
      continue;
    }
    out += s[i];
    i++;
  }
  return out;
}

/**
 * Slice of SQL from WHERE/HAVING/ON through next major clause (for AND/OR context).
 * @returns {{ text: string, start: number }[]}
 */
function predicateRegions(sql) {
  const s = stripCommentsKeepLen(sql);
  const regions = [];
  const startRe = /\b(WHERE|HAVING|ON)\b/gi;
  const endRe = /\b(GROUP|ORDER|HAVING|UNION|MINUS|INTERSECT|FETCH|OFFSET|CONNECT|START|FOR|WHERE)\b/gi;
  let m;
  while ((m = startRe.exec(s)) !== null) {
    const start = m.index + m[0].length;
    endRe.lastIndex = start;
    const endM = endRe.exec(s);
    let end = s.length;
    if (endM) end = endM.index;
    regions.push({ text: s.slice(start, end), start });
  }
  return regions;
}

/**
 * True if token after typo looks like start of a predicate.
 * Requires whitespace so AD.col (alias.column) is not treated as AND typo.
 */
export function looksLikePredicateStart(after) {
  if (!/^\s+/.test(after)) return false;
  const rest = after.trimStart();
  if (!rest) return false;
  if (/^(NOT|EXISTS|BETWEEN|IN|LIKE|IS)\b/i.test(rest)) return true;
  if (/^\(/.test(rest)) return true;
  if (/^[A-Za-z][A-Za-z0-9_$#]*\s*\./.test(rest)) return true;
  if (/^[A-Za-z][A-Za-z0-9_$#]*\s*(=|<>|!=|<|>|<=|>=|IS\b|IN\b|LIKE\b|BETWEEN\b|NOT\b)/i.test(rest)) {
    return true;
  }
  return false;
}

/** Short AND/OR near-misses — only inside WHERE/HAVING/ON with predicate context. */
const PREDICATE_CONN_TYPOS = [
  { wrong: 'AD', right: 'AND', why: 'missing N' },
  { wrong: 'AN', right: 'AND', why: 'missing D' },
  { wrong: 'ND', right: 'AND', why: 'missing A' },
  { wrong: 'ADN', right: 'AND', why: 'transposed letters' },
  { wrong: 'NAD', right: 'AND', why: 'transposed letters' },
  { wrong: 'ANDD', right: 'AND', why: 'extra D' },
  { wrong: 'AAND', right: 'AND', why: 'extra A' },
  { wrong: 'ANND', right: 'AND', why: 'extra N' },
  { wrong: 'RO', right: 'OR', why: 'transposed letters' },
  { wrong: 'ORR', right: 'OR', why: 'extra R' },
  { wrong: 'ROR', right: 'OR', why: 'transposed letters' },
];

/**
 * Clause / keyword typos safe to scan globally (whole-word).
 * Longer tokens preferred; short ambiguous tokens stay in PREDICATE_CONN_TYPOS.
 */
const CLAUSE_KEYWORD_TYPOS = [
  // SELECT
  { wrong: 'SELEC', right: 'SELECT', why: 'missing T' },
  { wrong: 'SELET', right: 'SELECT', why: 'missing C' },
  { wrong: 'SELCT', right: 'SELECT', why: 'missing E' },
  { wrong: 'SLECT', right: 'SELECT', why: 'missing E' },
  { wrong: 'SELCET', right: 'SELECT', why: 'transposed letters' },
  { wrong: 'SELLECT', right: 'SELECT', why: 'extra L' },
  { wrong: 'SLECTE', right: 'SELECT', why: 'transposed letters' },
  // DISTINCT (hand list + fuzzy generator below covers other 1-edit misses like DISTNCT)
  { wrong: 'DISTINC', right: 'DISTINCT', why: 'missing T' },
  { wrong: 'DISTICT', right: 'DISTINCT', why: 'missing N' },
  { wrong: 'DISTNCT', right: 'DISTINCT', why: 'missing I' },
  { wrong: 'DSTINCT', right: 'DISTINCT', why: 'missing I' },
  { wrong: 'DISINCT', right: 'DISTINCT', why: 'missing T' },
  { wrong: 'DISTINICT', right: 'DISTINCT', why: 'extra I' },
  { wrong: 'DINSTINCT', right: 'DISTINCT', why: 'transposed letters' },
  { wrong: 'DISTING', right: 'DISTINCT', why: 'wrong ending' },
  { wrong: 'DISTINT', right: 'DISTINCT', why: 'missing C' },
  { wrong: 'DISTINTC', right: 'DISTINCT', why: 'transposed letters' },
  // FROM
  { wrong: 'FORM', right: 'FROM', why: 'missing E / transposed', skipBefore: /^AT\b/i }, // FORMAT
  { wrong: 'FRO', right: 'FROM', why: 'missing M' },
  { wrong: 'FRROM', right: 'FROM', why: 'extra R' },
  { wrong: 'FOMR', right: 'FROM', why: 'transposed letters' },
  { wrong: 'RFOM', right: 'FROM', why: 'transposed letters' },
  { wrong: 'FOROM', right: 'FROM', why: 'extra O' },
  { wrong: 'FOM', right: 'FROM', why: 'missing R' },
  // WHERE
  { wrong: 'WERH', right: 'WHERE', why: 'transposed letters' },
  { wrong: 'WHER', right: 'WHERE', why: 'missing E' },
  { wrong: 'WHRE', right: 'WHERE', why: 'transposed letters' },
  { wrong: 'WHREE', right: 'WHERE', why: 'transposed letters' },
  { wrong: 'WEHRE', right: 'WHERE', why: 'transposed letters' },
  { wrong: 'HWERE', right: 'WHERE', why: 'transposed letters' },
  { wrong: 'WERHE', right: 'WHERE', why: 'transposed letters' },
  { wrong: 'WHEER', right: 'WHERE', why: 'transposed letters' },
  { wrong: 'WHERR', right: 'WHERE', why: 'extra R' },
  { wrong: 'WERE', right: 'WHERE', why: 'missing H', requireAfterIdent: true },
  // JOIN family
  { wrong: 'JION', right: 'JOIN', why: 'transposed letters' },
  { wrong: 'JONI', right: 'JOIN', why: 'transposed letters' },
  { wrong: 'JOIM', right: 'JOIN', why: 'wrong letter' },
  { wrong: 'INER', right: 'INNER', why: 'missing N' },
  { wrong: 'INNR', right: 'INNER', why: 'missing E' },
  { wrong: 'INNRE', right: 'INNER', why: 'transposed letters' },
  { wrong: 'INNERR', right: 'INNER', why: 'extra R' },
  { wrong: 'LFET', right: 'LEFT', why: 'transposed letters' },
  { wrong: 'LETF', right: 'LEFT', why: 'transposed letters' },
  { wrong: 'RIHGT', right: 'RIGHT', why: 'transposed letters' },
  { wrong: 'RIGH', right: 'RIGHT', why: 'missing T' },
  { wrong: 'RIGTH', right: 'RIGHT', why: 'transposed letters' },
  { wrong: 'OUTR', right: 'OUTER', why: 'missing E' },
  { wrong: 'OUTTER', right: 'OUTER', why: 'extra T' },
  { wrong: 'OUETER', right: 'OUTER', why: 'transposed letters' },
  { wrong: 'CROS', right: 'CROSS', why: 'missing S' },
  { wrong: 'CRSOSS', right: 'CROSS', why: 'transposed letters' },
  // GROUP / ORDER / HAVING
  { wrong: 'GROPU', right: 'GROUP', why: 'transposed letters' },
  { wrong: 'GORUP', right: 'GROUP', why: 'transposed letters' },
  { wrong: 'GRUOP', right: 'GROUP', why: 'transposed letters' },
  { wrong: 'GROOP', right: 'GROUP', why: 'wrong vowel' },
  { wrong: 'GRPUP', right: 'GROUP', why: 'transposed letters' },
  { wrong: 'ORDRE', right: 'ORDER', why: 'transposed letters' },
  { wrong: 'ODERR', right: 'ORDER', why: 'transposed letters' },
  { wrong: 'OREDER', right: 'ORDER', why: 'extra E' },
  { wrong: 'ORDR', right: 'ORDER', why: 'missing E' },
  { wrong: 'OREDR', right: 'ORDER', why: 'transposed letters' },
  { wrong: 'HAVNG', right: 'HAVING', why: 'missing I' },
  { wrong: 'HAVIN', right: 'HAVING', why: 'missing G' },
  { wrong: 'HAIVNG', right: 'HAVING', why: 'transposed letters' },
  { wrong: 'HAVIGN', right: 'HAVING', why: 'transposed letters' },
  // UNION / SET ops
  { wrong: 'UNIO', right: 'UNION', why: 'missing N' },
  { wrong: 'UINON', right: 'UNION', why: 'transposed letters' },
  { wrong: 'UNIOIN', right: 'UNION', why: 'extra I' },
  { wrong: 'UNIION', right: 'UNION', why: 'extra I' },
  // Predicates / null
  { wrong: 'EXITS', right: 'EXISTS', why: 'transposed letters' },
  { wrong: 'EXIST', right: 'EXISTS', why: 'missing S' },
  { wrong: 'EXSIST', right: 'EXISTS', why: 'extra S' },
  { wrong: 'EXSITS', right: 'EXISTS', why: 'transposed letters' },
  { wrong: 'BETWEN', right: 'BETWEEN', why: 'missing E' },
  { wrong: 'BETEEN', right: 'BETWEEN', why: 'missing W' },
  { wrong: 'BETWEAN', right: 'BETWEEN', why: 'wrong vowel' },
  { wrong: 'BTWEEN', right: 'BETWEEN', why: 'missing E' },
  { wrong: 'BEETWEEN', right: 'BETWEEN', why: 'extra E' },
  { wrong: 'LIEK', right: 'LIKE', why: 'transposed letters' },
  { wrong: 'LIK', right: 'LIKE', why: 'missing E' },
  { wrong: 'NTO', right: 'NOT', why: 'transposed letters' },
  { wrong: 'ONT', right: 'NOT', why: 'transposed letters' },
  { wrong: 'NOTT', right: 'NOT', why: 'extra T' },
  { wrong: 'NUL', right: 'NULL', why: 'missing L' },
  { wrong: 'NLL', right: 'NULL', why: 'missing U' },
  { wrong: 'NUUL', right: 'NULL', why: 'extra U' },
  { wrong: 'NULLE', right: 'NULL', why: 'extra E' },
  // CASE
  { wrong: 'CAS', right: 'CASE', why: 'missing E' },
  { wrong: 'WHN', right: 'WHEN', why: 'missing E' },
  { wrong: 'WEHN', right: 'WHEN', why: 'transposed letters' },
  { wrong: 'TEHN', right: 'THEN', why: 'transposed letters' },
  { wrong: 'THENN', right: 'THEN', why: 'extra N' },
  { wrong: 'ELS', right: 'ELSE', why: 'missing E' },
  { wrong: 'ESLE', right: 'ELSE', why: 'transposed letters' },
  // Misc
  { wrong: 'ASAS', right: 'AS', why: 'duplicated' },
  { wrong: 'BYBY', right: 'BY', why: 'duplicated' },
  { wrong: 'ONON', right: 'ON', why: 'duplicated' },
];

const OPERATOR_TYPOS = [
  { pattern: /==/g, wrong: '==', right: '=', why: 'Oracle uses single =' },
  { pattern: /\b=\s*=\b/g, wrong: '= =', right: '=', why: 'Oracle uses single =' },
];

/**
 * Longer SQL keywords — auto-generate 1-edit near-misses (delete / adjacent transpose / insert).
 * Short keywords stay curated to avoid false positives (HERE→WHERE, etc.).
 */
const FUZZY_KEYWORDS = [
  'SELECT',
  'DISTINCT',
  'BETWEEN',
  'EXISTS',
  'HAVING',
  'UNIQUE',
  'INTERSECT',
  'MINUS',
  'OFFSET',
  'NATURAL',
  'INNER',
  'OUTER',
  'RIGHT',
  'CROSS',
  'UNION',
  'GROUP',
  'ORDER',
  'WHERE',
  'FROM',
  'JOIN',
  'FETCH',
];

/** Tokens that are valid SQL — never treat as a typo of something else. */
const SQL_RESERVED = new Set(
  [
    'SELECT',
    'FROM',
    'WHERE',
    'AND',
    'OR',
    'NOT',
    'NULL',
    'AS',
    'ON',
    'IN',
    'IS',
    'BY',
    'SET',
    'ALL',
    'ANY',
    'SOME',
    'JOIN',
    'INNER',
    'LEFT',
    'RIGHT',
    'FULL',
    'OUTER',
    'CROSS',
    'NATURAL',
    'USING',
    'GROUP',
    'ORDER',
    'HAVING',
    'UNION',
    'MINUS',
    'INTERSECT',
    'DISTINCT',
    'UNIQUE',
    'EXISTS',
    'BETWEEN',
    'LIKE',
    'ESCAPE',
    'CASE',
    'WHEN',
    'THEN',
    'ELSE',
    'END',
    'WITH',
    'FETCH',
    'OFFSET',
    'ROWS',
    'ONLY',
    'ASC',
    'DESC',
    'INTO',
    'VALUES',
    'TABLE',
    'VIEW',
    'DUAL',
    'SYSDATE',
    'USER',
    'LEVEL',
    'ROWNUM',
    'ROWID',
    'CONNECT',
    'START',
    'PRIOR',
    'NOCYCLE',
    'PIVOT',
    'UNPIVOT',
    'MODEL',
    'PARTITION',
    'OVER',
    'WITHIN',
    'RECURSIVE',
    'FORMAT', // FORM + AT — never rewrite FORMAT
  ].map((k) => k.toUpperCase())
);

function generateEdit1Variants(keyword) {
  const k = keyword.toUpperCase();
  const out = new Set();
  for (let i = 0; i < k.length; i++) {
    out.add(k.slice(0, i) + k.slice(i + 1)); // deletion
  }
  for (let i = 0; i < k.length - 1; i++) {
    out.add(k.slice(0, i) + k[i + 1] + k[i] + k.slice(i + 2)); // adjacent transpose
  }
  // Insert a letter that already appears in the keyword (covers ANDD, SELLECT, …)
  const alphabet = new Set(k.split(''));
  for (let i = 0; i <= k.length; i++) {
    for (const ch of alphabet) {
      out.add(k.slice(0, i) + ch + k.slice(i));
    }
  }
  out.delete(k);
  return out;
}

let _nearMissMap = null;
function getNearMissMap() {
  if (_nearMissMap) return _nearMissMap;
  const map = new Map(); // wrongUpper -> { right, why }
  for (const kw of FUZZY_KEYWORDS) {
    if (kw.length < 4) continue;
    for (const v of generateEdit1Variants(kw)) {
      if (v.length < 4) continue;
      // Avoid very short wrongs from short keywords (HERE, ROM, …)
      if (v.length < 5 && kw.length <= 5) continue;
      if (SQL_RESERVED.has(v)) continue;
      const prev = map.get(v);
      // Prefer longer / closer target keyword
      if (!prev || prev.right.length < kw.length) {
        map.set(v, { right: kw, why: 'spelling near-miss' });
      }
    }
  }
  // Curated overrides / extras
  for (const item of CLAUSE_KEYWORD_TYPOS) {
    map.set(item.wrong.toUpperCase(), { right: item.right, why: item.why, skipBefore: item.skipBefore, requireAfterIdent: item.requireAfterIdent });
  }
  _nearMissMap = map;
  return map;
}

function lineSnippet(trimmed, index) {
  const lineStart = trimmed.lastIndexOf('\n', Math.max(0, index - 1)) + 1;
  let lineEnd = trimmed.indexOf('\n', index);
  if (lineEnd < 0) lineEnd = trimmed.length;
  return trimmed.slice(lineStart, lineEnd).replace(/\s+/g, ' ').trim();
}

function hitResult(trimmed, index, wrong, right, why) {
  const { line, column } = offsetToLineCol(trimmed, index);
  const snippet = lineSnippet(trimmed, index);
  const example =
    right === 'WHERE'
      ? `FROM table_name WHERE column = value`
      : right === 'AND' || right === 'OR'
        ? `… = 'US' ${right} TRUNC(SYSDATE) BETWEEN …`
        : `… ${right} …`;
  return {
    error:
      `Keyword typo at line ${line}, column ${column}: found "${wrong}" where "${right}" is required` +
      (why ? ` (${why})` : '') +
      (snippet ? `. Near: ${snippet}` : ''),
    suggestion:
      `Replace "${wrong}" with "${right}". Example: ${example}. ` +
      `Oracle often misreports this as ORA-00907 (missing right parenthesis) or another syntax error.`,
    line,
    column,
    index,
    wrong,
    right,
  };
}

function findOperatorTypo(trimmed) {
  for (const { pattern, wrong, right, why } of OPERATOR_TYPOS) {
    pattern.lastIndex = 0;
    const m = pattern.exec(trimmed);
    if (m) {
      const { line, column } = offsetToLineCol(trimmed, m.index);
      const snippet = lineSnippet(trimmed, m.index);
      return {
        error:
          `Invalid operator at line ${line}, column ${column}: found "${wrong}" where "${right}" is required` +
          (why ? ` (${why})` : '') +
          (snippet ? `. Near: ${snippet}` : ''),
        suggestion: `Change ${wrong} to ${right}. Example: WHERE column ${right} value`,
        line,
        column,
        index: m.index,
        wrong,
        right,
      };
    }
  }
  return null;
}

function findClauseKeywordTypo(trimmed) {
  const s = stripCommentsKeepLen(trimmed);
  const map = getNearMissMap();
  // Scan identifiers; check near-miss map (curated + auto 1-edit)
  const re = /\b([A-Za-z][A-Za-z0-9_$#]*)\b/g;
  let m;
  while ((m = re.exec(s)) !== null) {
    const word = m[1];
    const upper = word.toUpperCase();
    if (SQL_RESERVED.has(upper)) continue;
    const item = map.get(upper);
    if (!item) continue;
    if (upper === item.right.toUpperCase()) continue;
    const after = s.slice(m.index + word.length);
    if (after.startsWith('.')) continue; // alias.col
    // Alias after AS: SELECT x AS elect … — not a keyword typo
    const before = s.slice(Math.max(0, m.index - 6), m.index);
    if (/\bAS\s+$/i.test(before)) continue;
    if (item.skipBefore && item.skipBefore.test(after.trimStart())) continue;
    // FORM in FORMAT already blocked via SQL_RESERVED FORMAT + skipBefore on FORM
    if (upper === 'FORM' && /^AT\b/i.test(after.trimStart())) continue;
    if (item.requireAfterIdent) {
      if (!looksLikePredicateStart(after) && !/^\s+[A-Za-z_(]/.test(after)) continue;
    }
    if (after.length > 0 && !/^[\s,(]/.test(after)) continue;
    return hitResult(trimmed, m.index, word, item.right, item.why);
  }
  return null;
}

function findPredicateConnTypo(trimmed) {
  const regions = predicateRegions(trimmed);
  for (const region of regions) {
    for (const { wrong, right, why } of PREDICATE_CONN_TYPOS) {
      const re = new RegExp(`\\b(${wrong})\\b`, 'gi');
      let m;
      while ((m = re.exec(region.text)) !== null) {
        if (m[1].toUpperCase() === right) continue;
        const after = region.text.slice(m.index + m[1].length);
        if (after.startsWith('.')) continue;
        if (!looksLikePredicateStart(after)) continue;
        return hitResult(trimmed, region.start + m.index, m[1], right, why);
      }
    }
  }
  return null;
}

const PREDICATE_FN =
  '(?:TRUNC|TO_DATE|TO_CHAR|TO_NUMBER|NVL|NVL2|COALESCE|UPPER|LOWER|SUBSTR|LENGTH|COUNT|SUM|MAX|MIN|AVG|CAST|DECODE|GREATEST|LEAST|FLOOR|CEIL|ROUND|MONTHS_BETWEEN)';

const SKIP_PREV_TOKEN =
  /^(AND|OR|THEN|ELSE|WHEN|NOT|IN|IS|LIKE|BETWEEN|EXISTS|NULL|TRUE|FALSE|AS|ON|USING|SET|BY|ASC|DESC)$/i;

function missingConnectorHit(trimmed, abs, prev, nextTok) {
  const { line, column } = offsetToLineCol(trimmed, abs);
  const snippet = lineSnippet(trimmed, abs);
  return {
    error:
      `Missing AND or OR at line ${line}, column ${column}: the condition ending with ${prev} is immediately followed by a new condition starting with "${nextTok}", with no AND/OR between them` +
      (snippet ? `. Near: ${snippet}` : ''),
    suggestion:
      `Insert AND (or OR) before ${nextTok}. Example: … ${prev} AND ${nextTok}(…) … ` +
      `Without it, Oracle often returns ORA-00907 (missing right parenthesis) even though no parenthesis is wrong.`,
    line,
    column,
    index: abs,
    wrong: '',
    right: 'AND',
  };
}

/**
 * Missing AND/OR between two WHERE/HAVING/ON predicates.
 * Classic:  ... = 'US'   TRUNC(SYSDATE) BETWEEN ...
 * Avoid false positives when AND/OR is already present (including at end of prior line).
 */
export function findMissingPredicateConnector(trimmed) {
  const regions = predicateRegions(trimmed);

  // 1) Finished RHS (string / NULL / number), then new predicate — no AND/OR between
  const re =
    new RegExp(
      `((?:'[^']*'|\\bNULL\\b|\\b\\d+(?:\\.\\d+)?\\b))\\s+` +
        `(?!(?:AND|OR|THEN|ELSE|WHEN|END|GROUP|ORDER|HAVING|UNION|MINUS|INTERSECT|FETCH|OFFSET)\\b)` +
        `((?:${PREDICATE_FN}|[A-Za-z][A-Za-z0-9_$#]*)\\s*\\(|` +
        `[A-Za-z][A-Za-z0-9_$#]*\\s*\\.[A-Za-z][A-Za-z0-9_$#]*|` +
        `[A-Za-z][A-Za-z0-9_$#]*\\s*(?:=|<|>|IS\\b|IN\\b|LIKE\\b|BETWEEN\\b|NOT\\b))`,
      'gi'
    );

  for (const region of regions) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(region.text)) !== null) {
      const gap = region.text.slice(m.index + m[1].length, m.index + m[0].length - m[2].length);
      // Connector may sit in the gap with odd spacing/comments already stripped
      if (/\b(?:AND|OR)\b/i.test(gap)) continue;
      // Concatenation / arithmetic continuation: 'x' || …  or  1 + …
      if (/[|+\-*/]/.test(gap)) continue;
      const secondAt = m.index + m[0].length - m[2].length;
      const nextTok = (m[2].match(/^[A-Za-z][A-Za-z0-9_$#]*/i) || [m[2].trim()])[0];
      return missingConnectorHit(trimmed, region.start + secondAt, m[1], nextTok);
    }
  }

  // 2) Ident/qual.col at EOL, next line starts a function — but not when line ends with AND/OR
  const reNl = new RegExp(
    `(?<![A-Za-z0-9_$#.])(?!(?:AND|OR|THEN|ELSE|WHEN|NOT|IN|IS|LIKE|BETWEEN|EXISTS)\\b)` +
      `([A-Za-z][A-Za-z0-9_$#]*(?:\\.[A-Za-z][A-Za-z0-9_$#]*)*)\\s*\\r?\\n\\s+` +
      `(?!(?:AND|OR|THEN|ELSE|WHEN|END|GROUP|ORDER|HAVING|UNION)\\b)` +
      `((?:${PREDICATE_FN})\\s*\\()`,
    'gi'
  );

  for (const region of regions) {
    reNl.lastIndex = 0;
    let m;
    while ((m = reNl.exec(region.text)) !== null) {
      const prev = m[1];
      if (SKIP_PREV_TOKEN.test(prev)) continue;
      const before = region.text.slice(0, m.index);
      // RHS continuation: x =\n TRUNC(…)   or   AND\n TRUNC(…) already excluded via prev
      if (/[=<>]\s*$/.test(before) || /\b(?:IS|LIKE|IN|BETWEEN|NOT|AND|OR)\s*$/i.test(before)) {
        continue;
      }
      // Outer-join marker on prior token line: … id (+)\n trunc — still needs AND if new predicate
      const secondAt = m.index + m[0].length - m[2].length;
      const nextTok = (m[2].match(/^[A-Za-z]+/i) || ['expression'])[0];
      return missingConnectorHit(trimmed, region.start + secondAt, prev, nextTok);
    }
  }

  // 3) Closing paren at EOL, next line starts function without AND/OR
  //    … = '4712-12-31')\n trunc(…)   OR   … in (1, 2)\n trunc(…)
  // Skip: … )\n AND trunc   and   (= \n trunc as RHS of comparison before paren — rare
  const reParenNl = new RegExp(
    `(\\)(?:\\s*\\(\\+\\))?)\\s*\\r?\\n\\s+(?!(?:AND|OR|THEN|ELSE|WHEN|END|GROUP|ORDER|HAVING|UNION)\\b)((?:${PREDICATE_FN})\\s*\\()`,
    'gi'
  );
  for (const region of regions) {
    reParenNl.lastIndex = 0;
    let m;
    while ((m = reParenNl.exec(region.text)) !== null) {
      const before = region.text.slice(0, m.index);
      if (/\b(?:AND|OR)\s*$/i.test(before)) continue;
      const secondAt = m.index + m[0].length - m[2].length;
      const nextTok = (m[2].match(/^[A-Za-z]+/i) || ['expression'])[0];
      return missingConnectorHit(trimmed, region.start + secondAt, m[1], nextTok);
    }
  }

  return null;
}

/**
 * AND used where WHERE is required: FROM … alias AND col = …
 * (JOIN … ON a=b AND c=d is valid — skip when ON appears before this AND)
 */
function findAndUsedAsWhere(trimmed) {
  const s = stripCommentsKeepLen(trimmed);
  if (/\bWHERE\b/i.test(s)) return null;
  const fromIdx = s.search(/\bFROM\b/i);
  if (fromIdx < 0) return null;

  const andRe =
    /\bAND\b\s+(?=(?:NOT|EXISTS|[A-Za-z][A-Za-z0-9_$#]*\s*\.|[A-Za-z][A-Za-z0-9_$#]*\s*(?:=|<|>|IS\b|IN\b|LIKE\b|BETWEEN\b|NOT\b)|\())/gi;
  andRe.lastIndex = fromIdx;
  const m = andRe.exec(s);
  if (!m) return null;

  const between = s.slice(fromIdx, m.index);
  if (/\bON\b/i.test(between)) return null;
  return hitResult(trimmed, m.index, m[0].trim(), 'WHERE', 'filters after FROM need WHERE, not AND');
}

/**
 * @param {string} sql
 * @returns {null|{ error: string, suggestion: string, line: number, column: number, index: number, wrong: string, right: string }}
 */
export function findKeywordTypo(sql) {
  if (!sql || typeof sql !== 'string') return null;
  const trimmed = sql.trim();
  if (!trimmed) return null;

  return (
    findOperatorTypo(trimmed) ||
    findClauseKeywordTypo(trimmed) ||
    findPredicateConnTypo(trimmed) ||
    findMissingPredicateConnector(trimmed) ||
    findAndUsedAsWhere(trimmed) ||
    null
  );
}

